import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { PurchaseOrderDocumentDownloadButton } from '@/features/purchase-order/components/purchase-order-document-download-button';
import { PurchaseOrderDocumentTypeBadge } from '@/features/purchase-order/components/purchase-order-document-type-badge';
import { PurchaseOrderDocumentViewButton } from '@/features/purchase-order/components/purchase-order-document-view-button';
import { formatDate } from '@/lib/format';
import type { PurchaseOrderDocument } from '@/types/core/purchase-order/purchase-order-document';

export function usePurchaseOrderDocumentsTableColumns(): ColumnDef<PurchaseOrderDocument>[] {
  const { t, i18n } = useTranslation('purchaseOrder');
  const locale = i18n.language ?? 'fr';

  return useMemo(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('documentsTable.fileName')}
          />
        ),
        cell: ({ row }) => (
          <div className='max-w-md truncate' title={row.original.name}>
            {row.original.name}
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('documentsTable.fileName'),
        },
      },
      {
        id: 'type',
        accessorFn: (row) => row.documentType.code,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('documentsTable.type')}
          />
        ),
        cell: ({ row }) => (
          <PurchaseOrderDocumentTypeBadge
            documentTypeCode={row.original.documentType.code}
          />
        ),
        meta: {
          enableColumnReordering: false,
          label: t('documentsTable.type'),
        },
      },
      {
        id: 'userLogin',
        accessorKey: 'userLogin',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('documentsTable.user')}
          />
        ),
        cell: ({ row }) => <div>{row.original.userLogin}</div>,
        meta: {
          enableColumnReordering: false,
          label: t('documentsTable.user'),
        },
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('documentsTable.createdAt')}
          />
        ),
        cell: ({ row }) => (
          <div>
            {formatDate(row.original.createdAt, {
              locale,
            })}
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('documentsTable.createdAt'),
        },
      },
      {
        id: 'actions',
        accessorFn: (row) => row.clientUrl,
        header: ({ column }) => (
          <DataTableColumnHeader
            className='w-full justify-end'
            column={column}
            label={t('documentsTable.actions')}
          />
        ),
        cell: ({ row }) => {
          const { name, clientUrl } = row.original;
          if (!clientUrl) {
            return null;
          }
          return (
            <div className='flex justify-end gap-1'>
              <PurchaseOrderDocumentViewButton clientUrl={clientUrl} />
              <PurchaseOrderDocumentDownloadButton
                clientUrl={clientUrl}
                fileName={name}
              />
            </div>
          );
        },
        meta: {
          enableColumnReordering: false,
          label: t('documentsTable.actions'),
        },
      },
    ],
    [locale, t],
  ) satisfies ColumnDef<PurchaseOrderDocument>[];
}
